import React, { useState } from "react";
import { useNavigate } from "react-router";

import { Button } from "../../components/ui/button";
import { useNotification } from "../../contexts/NotificationContext";
import { Extra, ExtraType, extraSchema, flattenIssues } from "../../lib/pricing";
import { saveExtra } from "../pricing/repository";
import { FormSection, NumberField, SelectField, SwitchField, TextField } from "../components/pricing/fields";
import { PRICING_PATH } from "./ZoneForm";

interface Props {
  id?: string;
  extra?: Extra;
}

export default function ExtraForm({ id, extra }: Props) {
  const [name, setName] = useState(extra?.name ?? "");
  const [price, setPrice] = useState<number | "">(extra?.price ?? "");
  const [type, setType] = useState<ExtraType>(extra?.type ?? "fixed");
  const [active, setActive] = useState(extra?.active ?? true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const parsed = extraSchema.safeParse({ name, price: price === "" ? NaN : price, type, active });
    if (!parsed.success) {
      setErrors(flattenIssues(parsed.error));
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      await saveExtra(parsed.data, id);
      showNotification("success", id ? "Adicional atualizado." : "Adicional criado.");
      navigate(`${PRICING_PATH}?tab=extras`);
    } catch {
      showNotification("error", "Não foi possível salvar o adicional.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      <FormSection title="Adicional">
        <TextField
          id="extra-name"
          label="Nome *"
          placeholder="Ex.: Cadeirinha infantil"
          value={name}
          maxLength={80}
          onChange={setName}
          error={errors.name}
        />
        <NumberField
          id="extra-price"
          label="Preço (R$) *"
          min={0}
          step="1"
          value={price}
          onChange={setPrice}
          error={errors.price}
        />
        <SelectField
          label="Cobrança"
          value={type}
          onChange={(value) => setType(value as ExtraType)}
          options={[
            { value: "fixed", label: "Valor fixo por viagem" },
            { value: "perPax", label: "Por passageiro" },
          ]}
        />
        <SwitchField label="Adicional ativo" checked={active} onChange={setActive} />
      </FormSection>
      <Button type="submit" className="w-full" disabled={saving}>
        {id ? "Atualizar" : "Adicionar"}
      </Button>
    </form>
  );
}
